import React, { useEffect, useState } from "react"
import emailjs from "@emailjs/browser"
import { Link } from "react-router-dom"
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap"
import SoldxHeader from "../../../components/SoldxHeader"
import SoldxLandingFooter from "../../../components/SoldxLandingFooter"
import { SupportPhonePair } from "../../../components/SupportContactPhones"
import { getEmailJsConfig } from "../../../config/emailjsEnv"
import {
  SUPPORT_COMPANY_LEGAL_NAME,
  SUPPORT_EMAIL,
  SUPPORT_MAILTO,
  SUPPORT_WHATSAPP_WA_ME,
} from "../../../config/supportContact"
import { formatIndianDateTime } from "helpers/indianDate"
import "./ContactUs.scss"

type ContactFormState = {
  name: string
  contact: string
  email: string
  subject: string
  message: string
}

const initialForm: ContactFormState = {
  name: "",
  contact: "",
  email: "",
  subject: "",
  message: "",
}

const ContactUs = () => {
  const [form, setForm] = useState<ContactFormState>(initialForm)
  const [sending, setSending] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)

  const toggleSuccessModal = () => setSuccessModalOpen((open) => !open)

  useEffect(() => {
    document.title = "Get In Touch | SoldX"
  }, [])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const validateForm = (): string | null => {
    if (!form.name.trim()) return "Name is required"
    if (!form.contact.trim()) return "Contact is required"
    if (!form.email.trim()) return "Email is required"
    if (!form.subject.trim()) return "Subject is required"
    if (!form.message.trim()) return "Message is required"
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email"
    return null
  }

  const fallbackMailto = () => {
    const { name, contact, email, subject, message } = form
    const body = encodeURIComponent(
      `Name: ${name}\nContact: ${contact}\nEmail: ${email}\n\n${message}`
    )
    const subj = encodeURIComponent(subject.trim() || "Contact — SoldX")
    window.location.href = `${SUPPORT_MAILTO}?subject=${subj}&body=${body}`
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const err = validateForm()
    if (err) {
      alert(err)
      return
    }

    const cfg = getEmailJsConfig()
    if (!cfg) {
      alert(
        "EmailJS is not configured. Add REACT_APP_EMAILJS_* to .env (see .env.example), then restart the dev server. Opening your email app instead."
      )
      fallbackMailto()
      return
    }

    try {
      setSending(true)
      await emailjs.send(
        cfg.serviceId,
        cfg.templateId,
        {
          name: form.name.trim(),
          contact: form.contact.trim(),
          email: form.email.trim(),
          subject: form.subject.trim(),
          message: form.message.trim(),
          time: formatIndianDateTime(new Date()),
        },
        { publicKey: cfg.publicKey }
      )
      setForm(initialForm)
      setSuccessModalOpen(true)
    } catch (e) {
      console.error("EmailJS error:", e)
      alert("Could not send via EmailJS. Check template IDs / service, or use email below.")
      fallbackMailto()
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="soldx-landing soldx-contact-page">
      <SoldxHeader solid />
      <main className="soldx-contact-main">
        <div className="soldx-contact-inner">
          <div className="soldx-contact-back-wrap">
            <Link to="/" className="soldx-contact-back">
              <i className="mdi mdi-arrow-left me-1" aria-hidden />
              Back to home
            </Link>
          </div>

          <header className="soldx-contact-hero">
            <h1 className="soldx-contact-hero-title">Get In Touch</h1>
            <p className="soldx-contact-hero-sub">
              If you have any questions, please do not hesitate to send us a message. We aim to reply within 24 hours.
            </p>
          </header>

          <div className="soldx-contact-card">
            <Row className="g-4 g-lg-5">
              <Col lg={7}>
                <h2 className="soldx-contact-form-title">Contact Us</h2>
                <form className="soldx-contact-form" onSubmit={handleSubmit} noValidate>
                  <Row>
                    <Col md={6}>
                      <label className="soldx-contact-label" htmlFor="contact-name">
                        Name
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        className="soldx-contact-input"
                        placeholder="Your name"
                        value={form.name}
                        onChange={handleChange}
                        autoComplete="name"
                      />
                    </Col>
                    <Col md={6}>
                      <label className="soldx-contact-label" htmlFor="contact-phone">
                        Contact
                      </label>
                      <input
                        id="contact-phone"
                        name="contact"
                        type="tel"
                        className="soldx-contact-input"
                        placeholder="Phone number"
                        value={form.contact}
                        onChange={handleChange}
                        autoComplete="tel"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <label className="soldx-contact-label" htmlFor="contact-email">
                        Email
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        className="soldx-contact-input"
                        placeholder="Email address"
                        value={form.email}
                        onChange={handleChange}
                        autoComplete="email"
                      />
                    </Col>
                    <Col md={6}>
                      <label className="soldx-contact-label" htmlFor="contact-subject">
                        Subject
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        className="soldx-contact-input"
                        placeholder="Subject"
                        value={form.subject}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <label className="soldx-contact-label" htmlFor="contact-message">
                        Your Message
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        className="soldx-contact-textarea"
                        placeholder="Please Drop Message.."
                        value={form.message}
                        onChange={handleChange}
                        rows={5}
                      />
                    </Col>
                  </Row>
                  <div className="soldx-contact-submit-wrap">
                    <button
                      type="submit"
                      className="soldx-contact-submit"
                      disabled={sending}
                    >
                      {sending ? "Sending…" : "Submit"}
                    </button>
                  </div>
                </form>
              </Col>
              <Col lg={5}>
                <aside className="soldx-contact-sidebar">
                  <h3 className="soldx-contact-hours-title">Open Hours</h3>
                  <ul className="soldx-contact-hours">
                    <li>
                      <strong>Mon – Fri</strong>
                      <span>9:00 AM – 6:00 PM</span>
                    </li>
                    <li>
                      <strong>Saturday</strong>
                      <span>10:00 AM – 4:00 PM</span>
                    </li>
                    <li>
                      <strong>Sunday</strong>
                      <span>Closed</span>
                    </li>
                  </ul>

                  <h3 className="soldx-contact-sidebar-title">Info Location</h3>
                  <ul className="soldx-contact-info-list">
                    <li className="soldx-contact-info-item">
                      <i className="mdi mdi-office-building-outline" aria-hidden />
                      <span>{SUPPORT_COMPANY_LEGAL_NAME}</span>
                    </li>
                    <li className="soldx-contact-info-item">
                      <i className="mdi mdi-map-marker-outline" aria-hidden />
                      <span>Gurugram, 122001</span>
                    </li>
                    <li className="soldx-contact-info-item">
                      <i className="mdi mdi-phone-outline" aria-hidden />
                      <span>
                        <SupportPhonePair />
                      </span>
                    </li>
                    <li className="soldx-contact-info-item">
                      <i className="mdi mdi-email-outline" aria-hidden />
                      <a href={SUPPORT_MAILTO}>{SUPPORT_EMAIL}</a>
                    </li>
                  </ul>

                  <div className="soldx-contact-social-wrap">
                    <p className="soldx-contact-social-label">Find Us</p>
                    <div className="soldx-contact-social">
                      <a
                        href={SUPPORT_WHATSAPP_WA_ME}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="soldx-contact-social-wa"
                        aria-label="WhatsApp"
                      >
                        <i className="mdi mdi-whatsapp" aria-hidden />
                      </a>
                    </div>
                  </div>
                </aside>
              </Col>
            </Row>
          </div>
        </div>

        <div className="soldx-contact-cta">
          <div className="soldx-contact-cta-inner">
            <div className="soldx-contact-cta-text">
              <p className="soldx-contact-cta-heading">LOOKING TO SELL OR RENT YOUR PROPERTY?</p>
              <p className="soldx-contact-cta-sub">
                We will assist you in the best and comfortable property services for you.
              </p>
            </div>
            <Link to="/seller" className="soldx-contact-cta-btn">
              REQUEST A QUOTE
              <i className="mdi mdi-chevron-up" aria-hidden />
            </Link>
          </div>
        </div>
      </main>

      <SoldxLandingFooter />

      <Modal
        isOpen={successModalOpen}
        toggle={toggleSuccessModal}
        centered
        className="soldx-contact-success-modal"
        backdrop="static"
      >
        <ModalHeader toggle={toggleSuccessModal} className="soldx-contact-success-modal__header">
          Message sent
        </ModalHeader>
        <ModalBody className="soldx-contact-success-modal__body">
          Thank you for contacting us. We will get back to you soon.
        </ModalBody>
        <ModalFooter className="soldx-contact-success-modal__footer">
          <Button
            type="button"
            className="soldx-contact-success-modal__ok"
            onClick={toggleSuccessModal}
          >
            OK
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default ContactUs
